/* istanbul ignore file */

import React from 'react';
import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import { useVisibilityObserver } from '@noths/polaris-client-react-hooks';
import type { ClickCarouselLinkPayload } from '@noths/polaris-client-ribbons-design-system';
import { PillBarCarousel } from '@noths/polaris-client-ribbons-design-system';
import type { Link } from '@noths/polaris-dev-ts-types';

import type { FilterOptionValue, SelectedFilterOption } from 'src/types';
import { EventCategory } from 'src/types';
import type { FilterUid } from '../../constants/uids';
import * as styles from '../../styles/QuickFilters.styles';

const QUICK_FILTERS_VISIBILITY_THRESHOLD = 0.7;

const addOrRemove = <T,>(array: T[], item: T) =>
  array.includes(item) ? array.filter((c) => c !== item) : [...array, item];

export interface QuickFiltersProps {
  filterUid: FilterUid;
  filterLinks?: Link[];
  selectedFilterOptions?: string | number | FilterOptionValue[];
  onSelectFilterOption?: (selectedFilterOption: SelectedFilterOption) => void;
}

export const QuickFilters = ({
  filterUid,
  filterLinks,
  selectedFilterOptions = [],
  onSelectFilterOption,
}: QuickFiltersProps) => {
  const { observedElementRef: quickFiltersRef } = useVisibilityObserver(
    () => {
      sendGAEvent({
        event: 'custom_event',
        event_category: 'Quick filters',
        event_action: 'quick filter impression',
        event_label: 'delivery',
      });
    },
    { observerOptions: { threshold: QUICK_FILTERS_VISIBILITY_THRESHOLD } },
  );

  const handleLinkClick = (
    evt: React.MouseEvent<HTMLAnchorElement>,
    { link: { href: optionValue } }: ClickCarouselLinkPayload,
  ) => {
    evt.preventDefault();
    if (!optionValue) {
      return;
    }

    const filterOptionValue = Array.isArray(selectedFilterOptions)
      ? addOrRemove(selectedFilterOptions, optionValue)
      : optionValue;

    onSelectFilterOption?.({
      filterUid,
      filterOptionValue,
      eventCategory: EventCategory.QuickFilters,
    });
  };

  if (!filterLinks) {
    return null;
  }

  return (
    <div css={styles.quickFilters} ref={quickFiltersRef}>
      <PillBarCarousel
        id="delivery-quick-filters"
        title="Quick filters"
        carouselType={filterUid}
        activeLinks={selectedFilterOptions}
        links={filterLinks}
        onLinkClick={handleLinkClick}
      />
    </div>
  );
};
